<!-- Form input for a person's height as a numeric value with a selectable unit (cm/other). -->

<script lang="ts" setup>
import { createFormField, formFieldEmits, formFieldProps } from '@nzyme/vue-forms/components/defineFormField.js';
import type { FormFieldController } from '@nzyme/vue-forms/components/defineFormField.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { vFocus } from '@nzyme/vue-utils/directives/vFocus.js';
import type { Slots } from '@nzyme/vue-utils/slots.js';
import { ChevronDown } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import type { PropType } from 'vue';

import type {
  ScalarMeasurementDefinition,
  ScalarMeasurementValue,
} from '@sigma/common/measurements/ScalarMeasurement.js';
import { UnitTokenTranslation } from '@sigma/common/measurements/UnitTokenTranslation.js';
import type { UnitToken } from '@sigma/common/measurements/UnitTokenTranslation.js';

import FormFieldWrapper from './FormFieldWrapper.vue';

const props = defineProps({
  ...formFieldProps<ScalarMeasurementValue | null>(),
  /**
   * The scalar measurement to collect (e.g. `BodyHeight`, `BodyWeight`). Drives the offered units.
   * Typed with the full `UnitToken` domain so `t(UnitTokenTranslation, unit)` type-checks in the
   * template — distributive `Translation<T>` rejects narrow unit unions.
   */
  definition: { type: Object as PropType<ScalarMeasurementDefinition<UnitToken>>, required: true },
  focus: Boolean,
  disabled: Boolean,
  readonly: Boolean,
  required: Boolean,
});

defineEmits(formFieldEmits<ScalarMeasurementValue | null>());

defineSlots<
  Slots<{
    info: { field: FormFieldController<ScalarMeasurementValue | null> };
  }>
>();

const field = createFormField<ScalarMeasurementValue | null>({ props });
const t = useTranslate();

const placeholderText = computed(() => t(props.definition.label));
const unitLabelText = computed(() => t(props.definition.unitLabel));

/** Local ref to track selected unit independently of field value. */
const selectedUnit = ref<string>(field.value?.unit ?? props.definition.unitTokens[0]);

/** Sync selectedUnit when field.value.unit changes externally (e.g., async prefill or reset). */
watch(
  () => field.value?.unit,
  newUnit => {
    selectedUnit.value = newUnit ?? props.definition.unitTokens[0];
  },
);

// Separate reactive values for the numeric input and unit
const numericValue = computed({
  get() {
    return field.value?.value ?? null;
  },
  set(newValue: number | null) {
    if (newValue == null || Number.isNaN(newValue)) {
      field.value = null;
    } else {
      field.value = {
        value: newValue,
        unit: selectedUnit.value,
      };
    }
  },
});

const unitValue = computed({
  get() {
    return selectedUnit.value;
  },
  set(newUnit: string) {
    selectedUnit.value = newUnit;

    if (field.value != null) {
      field.value = {
        ...field.value,
        unit: newUnit,
      };
    }
  },
});

function onFocus() {
  field.focused = true;
}

function onBlur() {
  field.focused = false;
}
</script>

<template>
  <FormFieldWrapper
    :field="field"
    :required="required"
    :disabled="disabled"
    :readonly="readonly"
  >
    <template #default="{ inputId }">
      <input
        :id="inputId"
        v-model.number="numericValue"
        v-focus="focus"
        type="number"
        :class="css.input"
        min="0"
        :step="definition.step"
        :disabled="disabled"
        :readonly="readonly"
        :placeholder="placeholderText"
        @focus="onFocus"
        @blur="onBlur"
      />
    </template>

    <template #addon>
      <div :class="css.unitSelector">
        <select
          v-model="unitValue"
          :class="css.select"
          :disabled="disabled || readonly"
          :aria-label="unitLabelText"
          @focus="onFocus"
          @blur="onBlur"
        >
          <option
            v-for="unit in definition.unitTokens"
            :key="unit"
            :value="unit"
          >
            {{ t(UnitTokenTranslation, unit) }}
          </option>
        </select>
        <ChevronDown :class="css.chevron" />
      </div>
    </template>

    <template #info>
      <slot
        name="info"
        :field="field"
      />
    </template>
  </FormFieldWrapper>
</template>

<style lang="scss" module="css">
.unitSelector {
  display: flex;
  align-items: center;
  position: relative;
  height: 100%;
}

.input {
  /* Hide number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  -moz-appearance: textfield;
  appearance: textfield;
}

.select {
  appearance: none;
  border: none;
  background: transparent;
  padding: 0 $spacing-24 0 $spacing-08 !important;
  font-size: $font-size-16;
  color: $color-gray-600;
  cursor: pointer;
  outline: none;
  height: 100%;

  &:disabled {
    cursor: not-allowed;
    color: $color-gray-600;
  }
}

.chevron {
  position: absolute;
  right: 0;
  margin-top: $spacing-02;
  width: 1.2em;
  height: 1.2em;
  color: $color-gray-600;
  pointer-events: none;
}
</style>
